/**
  @format
  @flow
 */

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Constants } from '@ziynx/components/layout';
import { VideoPlayer, ImageOverlay } from '@ziynx/components/media';

export type Props = $ReadOnly<{|
    /**
     * Boolean to allow for overriding the overlay
     */
    hideOverlay?: boolean,
    /**
     * Optional width,
     */
    width?: string,
    /**
     * Video source
     */
    src?: string,
    /**
     * Image overlay source
     */
    imageSource?: string,
    /**
     * Allow to pass in ref. Optional
     */
    getRef?: any,
    /**
     * Custom ononTimeUpdate function to grab
     * video's current playback time
     */
    onTimeUpdate?: () => void,
       /**
   * Custom function fired when video is played
   */
    onPlay?: () => void,
    /**
     * Custom function fired when user pauses video
     */
    onPause?: () => void,
    /**
     * Custom onClick function fired when user taps
     * on the imageOverlay
     */
    onClick?: () => void,
    /**
     * Custom function fired when video ends
     */
    onEnd?: () => void,
    /**
     * Fires on seek
     */
    onSeek?: () => void,
    /**
     * Fires on seek end
     */
    onSeekEnd?: () => void,
    /**
     * Sets Aspect Ratio for ImageWrapper
     * Note: Needs to have width or height of video set to 'auto' or be unset
     */
    aspectRatio?: string,
    /**
     * Position of play button
     */
    playButtonPosition?: 'bottomRight' | 'center',
  |}>;

const fallbackPadding = (ratio) => {
  switch (ratio) {
  case '16 / 9':
  case '16:9':
    return '56.25%';
  case '4 / 3':
  case '4:3':
    return '75%';
  case '1 / 1':
  case '1:1':
  case '1':
  default:
    return '100%';
  }
};

const Wrapper = styled.div`
  width: ${({ width }) => (width || '100%')};
  position: relative;
`;

const VideoWrapper = styled.div`
    display: flex;
    justify-content: center;
    z-index: 0;
    border-radius: ${Constants?.borderRadiusLarge}px;
    overflow: hidden;
    cursor: pointer;
    width: 100%;
    aspect-ratio: ${({ aspectRatio }) => aspectRatio || '1 / 1'};
    -webkit-backface-visibility: hidden;
    -webkit-transform: translate3d(0, 0, 0);
    @supports not (aspect-ratio: auto) {
      position: relative;
      padding-top: ${({ aspectRatio }) => (aspectRatio ? fallbackPadding(aspectRatio) : '100%')};
      height: 0;
    }
  `;

const ImageWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    border-radius: ${Constants?.borderRadiusLarge}px;
    overflow: hidden;
    cursor: pointer;
    width: 100%;
    aspect-ratio: ${({ aspectRatio }) => aspectRatio || '1 / 1'};
    @supports not (aspect-ratio: auto) {
       padding-top: ${({ aspectRatio }) => (aspectRatio ? fallbackPadding(aspectRatio) : '100%')};
       height: 0;
    };
    ${({ noVideoSource }) => noVideoSource && 'background-color: white;'};
  `;

const StyledVideoPlayer = styled(VideoPlayer)`
   max-height: 100%;
   max-width: 100%;
     @supports not (aspect-ratio: auto) { 
      position: absolute;
      transform: translate(-50%, -50%);
      left: 50%;
      top: 50%;
      width: 100%;
      max-width: 100%; 
      height: auto;
    };
  `;

const CardVideo = (props: Props): React$Node => {
  const {
    width,
    src,
    imageSource,
    getRef,
    onClick,
    hideOverlay,
    onEnd,
    onTimeUpdate,
    onSeekEnd,
    onSeek,
    onPlay,
    aspectRatio,
    playButtonPosition,
    ...rest
  } = props;

  let videoRef = useRef(null);
  const [showOverlay, toggleOverlay] = useState(true);

  /** Tap on overlay to disable overlay and play video */
  const _handlePlayVideo = () => {
    if (onClick) onClick();
    // Allows user to replay video once reach the end
    if (videoRef?.current?.ended) {
      toggleOverlay(false);
    }
    videoRef?.current?.play();
  };

  const _onVideoEnd = () => {
    toggleOverlay(true);
    const video: ?HTMLVideoElement = videoRef?.current;
    // Prepare to start at beginning on next click
    if (video && video?.currentTime !== 0) {
      video.pause();
      video.currentTime = 0;
    }
    if (onEnd) onEnd();
  };

  /**
     * Watch to see if hideOverlay is passed.
     * Toggles the overlay then plays the video
     * Used in TalentProfile page.
     */
  useEffect(() => {
    if (typeof hideOverlay === 'boolean') {
      // If the hideOVerlay is undefined this wont override
      // Should be the opposite since we are choosing hide to be true
      toggleOverlay(!hideOverlay);
    }
  }, [hideOverlay]);

  /** Pass ref */
  const _getRef = () => {
    if (getRef) {
      videoRef = getRef;
    }
    return videoRef;
  };

  if (!src || !imageSource) return null;
  return (
    <Wrapper width={width}>
      <VideoWrapper aspectRatio={aspectRatio}>
        <StyledVideoPlayer
          getRef={_getRef()}
          src={src}
          onEnd={_onVideoEnd}
          onTimeUpdate={onTimeUpdate}
          onSeekEnd={onSeekEnd}
          onSeek={onSeek}
          onPlay={onPlay}
          width="auto"
          onLoadedData={() => {
            toggleOverlay(false);
          }}
          {...rest}
        />
      </VideoWrapper>
      {showOverlay && (
        <ImageWrapper
          onClick={_handlePlayVideo}
          aspectRatio={aspectRatio}
          noVideoSource={src === ' '}
        >
          <ImageOverlay
            imageSource={imageSource}
            icon={{
              name: 'play-circle',
              small: true,
              padding: '16px',
              position: playButtonPosition || 'bottomRight',
            }}
          />
        </ImageWrapper>
      )}
    </Wrapper>
  );
};

CardVideo.defaultProps = {
  hideOverlay: false,
  aspectRatio: '1 / 1',
};

export default CardVideo;
